import moment from "moment"
import store from "../../../../store"
import { Axios, Network } from "../../../../util"
import { states } from "../../../constants"
import { Display, formatDate, formatPhone } from "../../../formating"
import { User } from "../profile"
import api from "../../../../../api/axios"

export default class Employee extends User {
	static async uploadEmployeeFile({ file, params }) {
		const { data } = await Network.AxiosWrapperV2.upload(`employer/${params.employerId}/userFile`, file)

		if (data?.links?.process?.url) {
			await Network.AxiosWrapperV2.send({ ...data?.links?.process })
			store.setNotification("notification", true, {
				msg: `Successfully uploaded employee file and processed employees.`
			})
		}
	}

	static async getAllWorkers({ params }) {
		const employees = await this.getAll({ params })
		let employeesMap = new Map()
		employeesMap = employees
		return employeesMap
	}

	static async getAll({ params }) {
		const { employerId, permissibleClasses, view } = params

		const response = await Promise.all([
			api.get(`/employer/${employerId}/employee`),
			api.get(
					view === "all-plans"
						? `/employer/${employerId}/employee/plan/v2/allPlans`
						: `/employer/${employerId}/employee/plan`)
		])

		const employees = response?.[0]?.data?.data
		const plans = response?.[1]?.data?.data

		const plansByUserId = {}
		plans.forEach((plan) => {
			const { userId } = plan
			let permissibleClass = {}

			if (permissibleClasses?.length && plan?.permissibleClassId) {
				permissibleClass = permissibleClasses.find((c) => c.id === plan.permissibleClassId)
			}

			if (plansByUserId[userId]) {
				plansByUserId[userId].push(new Network.API.Profile.PlanModel({ ...plan, permissibleClass }))
			} else {
				plansByUserId[userId] = [new Network.API.Profile.PlanModel({ ...plan, permissibleClass })]
			}
		})

		const employeesMap = new Map()
		employees.forEach((employee) => {
			const { userId } = employee
			employeesMap.set(
				userId,
				new this({
					...employee,
					isEnrolledInPlan: plansByUserId[userId]?.length > 0,
					plans: plansByUserId[userId]
				})
			)
		})

		return employeesMap
	}

	static async getEmployee({ employeeId, permissibleClasses }) {
		let employerId = store?.user?.employerAdminEmployer?.id
		if (!employerId) {
			employerId = store?.user?.employer?.id
		}
		const employeeURL = `employer/${employerId}/user/${employeeId}?socialSecurityLast4Only=true`
		const plansURL = `/employer/${employerId}/employee/${employeeId}/plan`

		const response = await Promise.all([api.get(employeeURL), api.get(plansURL)])

		const employee = response?.[0]?.data
		let plans = response?.[1]?.data?.data

		plans = plans.map((plan) => {
			const classId = plan?.permissibleClassId
			const permissibleClass = permissibleClasses?.find(({ id }) => id === classId) || {}
			return new Network.API.Profile.PlanModel({ ...plan, permissibleClass })
		})

		return new Employee({ ...employee, plans })
	}

	static async checkEmail({ user, params }) {
		const {
			data: {
				data: [employee]
			}
		} = await Network.AxiosWrapper({
			method: "GET",
			target: `employer/${user?.employer?.id}/employee?email=${params.email}`,
			token: user.token
		})
		return employee ? new this(employee) : null
	}

	static async createOne({ user, params, payload, history }) {
		const url = `employer/${params?.employerId}/user`
		const response = await api.post( url, payload)
		const employee = new Employee({ ...response?.data, plans: [] })

		const message = `Successfully added ${employee?.person?.firstName} ${employee?.person?.lastName}`
		store.setNotification("notification", true, { msg: message })

		history(-1)
		return employee
	}

	static async updateOne({ params: { employee }, payload, history }) {
		console.log("In updateOne, employee: ", employee)
		const { data: newEmployeeData } = await Network.AxiosWrapperV2.send({
			...employee.links?.update,
			data: payload
		})

		const newEmployee = new Employee({
			...newEmployeeData?.person,
			plans: []
		})

		store.setNotification("notification", true, {
			msg: `Successfully updated employee details`
		})

		history(-1)
		return newEmployee
	}

	static async inviteOne({ url, name }) {
		await api.post(url)
		const message = `Successfully invited ${name}`
		store.setNotification("notification", true, { msg: message })
	}

	static async inviteAll({ employerPlanId, planType }) {
		try {
			const url = `/employeeInvite/employerPlan/${employerPlanId}`
			await api.post(url)
			const message = `Successfully sent ${planType} invitations to all eligible employees`
			store.setNotification("notification", true, { msg: message })
		} catch (error) {
			console.error(error)
		}
	}

	static async createPlan({ planType, employerPlanId, employeeId, employeeName, payload }) {
		const url = `employee/${employeeId}/plan/eligible/${employerPlanId}`
		await api.post(url,payload )
		const message = `Successfully set ${employeeName} as eligible for ${planType}.`
		store.setNotification("notification", true, { msg: message })
	}

	static async unenrollPlan({ user, params }) {
		try {
			await Network.AxiosWrapper({
				method: params.plan.links.unenroll.method,
				target: params.plan.links.unenroll.url,
				token: user.token,
				options: { disablePrefix: true }
			})

			store.setNotification("notification", true, {
				msg: `Unenrolled ${params.plan.type} for ${params.employee.name}.`
			})
		} catch (err) {
			console.error(err)
		}
	}

	static async updatePlan({ url, payload }) {
		await api.patch(url,payload)
		return true
	}

	static async createAdmin({ params, data }) {
		const { data: employerAdminData } = await Network.AxiosWrapperV2.post({
			url: `employer/${params.employerId}/admins`,
			data
		})
		store.setNotification("notification", true, {
			msg: `Successfully created admin.`
		})
		return employerAdminData
	}

	get formValues() {
		return {
			email: this.email,
			firstName: this.firstName,
			lastName: this.lastName,
			phone: this.phone,
			birthday: this.dateOfBirth
				? formatDate({
						data: this.dateOfBirth,
						actions: ["new-date-object"],
						options: ["append-timezone"]
				  })
				: undefined,
			addressLine1: this.addressLine1,
			addressLine2: this.addressLine2,
			city: this.city,
			state: states[this.state],
			zip: this.zip,
			socialSecurityNumber: "",
			startDate: this.employment?.startDate
				? formatDate({
						data: this.employment?.startDate,
						actions: ["new-date-object"],
						options: ["append-timezone"]
				  })
				: undefined,
			insuranceDate: this?.insuranceDate
				? formatDate({
						data: this?.insuranceDate,
						actions: ["new-date-object"],
						options: ["append-timezone"]
				  })
				: undefined
		}
	}

	get formattedAddress() {
		return `${this.addressLine1 || ""} ${this.addressLine2 || ""}\n${this.city || ""} ${this.state || ""} ${
			this.zip || ""
		}`
	}

	get plansString() {
		return (
			Object.keys(this?.currentPlansByType)
				?.map((item) => item)
				?.join(", ") || ""
		)
	}

	get tableValues() {
		return {
			name: this.name,
			verifiedAccount: formatDate({
				data: this.emailValidatedTs,
				actions: ["display"]
			}),
			employmentStatus: Boolean(this.employment?.terminationDate) ? "Closed" : "Active",
			closedDate: formatDate({
				data: this.employment?.terminationDate,
				actions: ["display"],
				options: ["append-timezone"],
				defaultReturn: ""
			}),
			email: this.email,
			phone: formatPhone({ phone: this.phone || "", actions: ["extension"] }),
			phoneNumber: formatPhone({
				phone: this.phone || "",
				actions: ["extension"]
			}),
			birthday: this.dateOfBirth
				? formatDate({
						data: this.dateOfBirth,
						actions: ["new-date-object"],
						options: ["append-timezone"]
				  })
				: "",
			birthdayDate: this.dateOfBirth
				? formatDate({
						data: this.dateOfBirth,
						actions: ["display"]
				  })
				: "",
			insuranceStartDate: formatDate({
				data: this?.insuranceDate,
				actions: ["display"],
				options: ["append-timezone"]
			}),
			insuranceDate: this?.insuranceDate,
			hireDate: formatDate({
				data: this?.employment?.startDate,
				actions: ["display"],
				options: ["append-timezone"]
			}),
			roles: Display.EmployeeRoles({ roles: this.roles }),
			addressLine1: this?.addressLine1,
			addressLine2: this?.addressLine2,
			city: this?.city,
			zip: this?.zip,
			state: this?.state,
			accountStatus: this.accountStatus?.text,
			accountStatusDate: formatDate({
				data: this.accountStatus?.date,
				actions: ["display"],
				defaultReturn: ""
			}),
			terminationDate: formatDate({
				data: this?.employment?.terminationDate || this.terminationDate,
				actions: ["display"],
				options: ["append-timezone"]
			}),
			salusionEnrollmentStatus: Boolean(this?.isEnrolledInPlan) ? "Eligible" : "Not Eligibile",
			employmentActive: this.getEmploymentActive(this.employment?.terminationDate)
		}
	}

	async delete({}) {
		try {
			if (this.links?.delete) {
				await Network.AxiosWrapperV2.send({
					...this.links.delete
				})
				store.setNotification("notification", true, {
					msg: `Successfully deleted ${this.name}`
				})
				return true
			}
		} catch (err) {
			console.error(err)
		}
		return false
	}

	async terminate({ data }) {
		try {
			if (this.links?.terminate) {
				await Network.AxiosWrapperV2.send({
					...this.links.terminate,
					data
				})
				store.setNotification("notification", true, {
					msg: `Successfully terminated employment for ${this.name}`
				})
				return true
			}
		} catch (err) {
			console.error(err)
		}
		return false
	}

	async undoTermination({}) {
		try {
			if (this.links?.undoTermination) {
				await Network.AxiosWrapperV2.send({
					...this.links.undoTermination
				})
				store.setNotification("notification", true, {
					msg: `Successfully removed termination date for employee ${this.name}`
				})
				return true
			}
		} catch (err) {
			console.error(err)
		}
		return false
	}

	getPlanDefaultStartDate({ employerPlan }) {
		const employerPlanStartDate = formatDate({
			data: employerPlan.startDate,
			actions: ["new-date-object"],
			options: ["append-timezone"]
		})
		let employeeInsuranceDate = this?.employment?.startDate
			? formatDate({
					data: this?.employment?.startDate,
					actions: ["new-date-object"],
					options: ["append-timezone"]
			  })
			: null

		if (employeeInsuranceDate && employeeInsuranceDate?.getDate() !== 1) {
			employeeInsuranceDate = moment(employeeInsuranceDate).startOf("M").add(1, "M").toDate()
		}

		let firstOfMonth = moment().startOf("M").toDate()
		if (employerPlan.type === "HFSA" || employerPlan.type === "DCFSA") {
			if (moment().day() > 1) {
				firstOfMonth = moment().startOf("M").add(1, "M").toDate()
			}
		}

		let startDateToUse = {
			date: employerPlanStartDate,
			string: "Plan Start Date"
		}

		if (employeeInsuranceDate) {
			startDateToUse =
				employeeInsuranceDate > employerPlanStartDate
					? {
							date: employeeInsuranceDate,
							string: ""
					  }
					: {
							date: employerPlanStartDate,
							string: "Plan Start Date"
					  }
		}

		let defaultDate = startDateToUse.date
		if (firstOfMonth > startDateToUse.date && !employerPlan?.midYearPlan) defaultDate = firstOfMonth
		return defaultDate
	}
}
